import { makeStyles, mergeClasses } from '@fluentui/react-components';
import { useRef, useState } from 'react';

import { formatHM, HOURS_QUANTUM } from '../common';
import { HoursInput } from '../components';
import { palette, tokens } from '../theme';

export type EditableHoursProps = {
    hours: number;
    // The most this entry may hold, given the rest of the day.
    max: number;
    onChange: (hours: number) => void;
    className?: string;
};

// The hours as text; a click opens the same editor as the plan rows. Steps and
// typing change a draft, which Enter or a click outside applies and Escape drops.
export const EditableHours: React.FC<EditableHoursProps> = ({ hours, max, onChange, className }) => {
    const classes = useStyles();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(hours);
    // Read on blur, right after the editor's own commit sets it.
    const draftRef = useRef(hours);
    // Escape unmounts the editor; a blur after that must not apply.
    const finished = useRef(false);

    const startEditing = () => {
        finished.current = false;
        draftRef.current = hours;
        setDraft(hours);
        setEditing(true);
    };

    const changeDraft = (next: number) => {
        draftRef.current = next;
        setDraft(next);
    };

    const finish = (apply: boolean) => {
        if (finished.current) {
            return;
        }
        finished.current = true;
        setEditing(false);
        if (apply && draftRef.current !== hours) {
            onChange(draftRef.current);
        }
    };

    if (editing) {
        return (
            <HoursInput
                hours={draft}
                onChange={changeDraft}
                step={HOURS_QUANTUM}
                min={HOURS_QUANTUM}
                max={max}
                ariaLabel="Hours"
                focusOnMount
                onBlur={() => finish(true)}
                onCancel={() => finish(false)}
            />
        );
    }

    return (
        <button type="button" className={mergeClasses(className, classes.value)} title="Edit hours" onClick={startEditing}>
            {formatHM(hours)}
        </button>
    );
};

const useStyles = makeStyles({
    value: {
        padding: '2px 6px',
        margin: '-2px -6px',
        border: 'none',
        borderRadius: tokens.borderRadiusMedium,
        background: 'transparent',
        font: 'inherit',
        cursor: 'text',
        '&:hover': {
            backgroundColor: palette.panelMuted,
        },
        '&:focus-visible': {
            outline: `2px solid ${palette.accent}`,
            outlineOffset: '1px',
        },
    },
});
